/**
 * Modal Component - The Intelligent Standard
 *
 * Sophisticated modal with arena animation system.
 */

import { ReactNode, useEffect } from 'react';

import { Button } from './Button';

export interface ModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: string;
  showClose?: boolean;
}

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  maxWidth = '600px',
  showClose = true,
}: ModalProps) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 md:p-8 modal-backdrop-arena"
      style={{
        background: 'rgba(26, 26, 26, 0.6)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        className="w-full max-h-[90vh] overflow-hidden rounded-xl modal-content-arena"
        style={{
          maxWidth,
          background: '#FFFFFF',
          border: '1px solid #E8E0D8',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.16)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-5 py-4"
          style={{ borderBottom: '1px solid #E8E0D8' }}
        >
          <div>
            <h2
              className="font-serif text-xl font-medium m-0"
              style={{ color: '#1A1A1A' }}
            >
              {title}
            </h2>
            {subtitle && (
              <p className="text-xs mt-1 m-0" style={{ color: '#6B6B6B' }}>
                {subtitle}
              </p>
            )}
          </div>
          {showClose && (
            <button
              onClick={onClose}
              className="btn-arena -mt-1 -mr-1 p-2 rounded-lg"
              style={{
                background: 'transparent',
                color: '#6B6B6B',
                border: 'none',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#F5F0EB';
                e.currentTarget.style.color = '#1A1A1A';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6B6B6B';
              }}
              aria-label="Close modal"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div
          className="px-5 py-4 overflow-y-auto"
          style={{ maxHeight: 'calc(90vh - 80px)' }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Confirmation Modal variant
 */
export interface ConfirmModalProps {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'default';
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export function ConfirmModal({
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
  loading = false,
}: ConfirmModalProps) {
  return (
    <Modal title={title} onClose={onCancel} maxWidth="420px">
      <p className="mb-6" style={{ color: '#6B6B6B' }}>
        {message}
      </p>
      <div className="flex gap-3 justify-end">
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          {cancelText}
        </Button>
        <Button
          variant={variant === 'danger' ? 'danger' : 'primary'}
          onClick={onConfirm}
          loading={loading}
        >
          {confirmText}
        </Button>
      </div>
    </Modal>
  );
}
